// /frontend/src/pages/CustomDesignConfirmation.jsx
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Layout from '../components/layout/Layout';
import Spinner from '../components/ui/Spinner';
import { uploadService } from '../services/uploadService'; // Fixed import

const CustomDesignConfirmation = () => {
  const { requestId } = useParams();
  const { user } = useSelector(state => state.auth);
  
  const [designRequest, setDesignRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDesignRequest = async () => {
      try {
        const data = await uploadService.getDesignRequest(requestId); // Fixed method call
        setDesignRequest(data);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load design request details');
      } finally {
        setLoading(false);
      }
    };

    if (requestId) {
      fetchDesignRequest();
    }
  }, [requestId]);

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-64">
          <Spinner />
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-md mb-6">
            <p>{error}</p>
          </div>
          <Link to="/custom-design" className="text-teal-600 hover:text-teal-800">
            ← Back to Design
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="bg-white border border-gray-200 rounded-lg p-8 shadow-md">
          <div className="flex items-center justify-center mb-6">
            <div className="bg-teal-100 p-3 rounded-full">
              <svg className="h-10 w-10 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
              </svg>
            </div>
          </div>
          
          <h1 className="text-3xl font-bold text-center mb-2">Design Request Submitted!</h1>
          <p className="text-center text-gray-600 mb-8">
            Your floor plan has been successfully uploaded and your design request is now being processed.
          </p>
          
          <div className="bg-gray-50 rounded-lg p-6 mb-8">
            <h2 className="text-xl font-semibold mb-4">Design Request Details</h2>
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-3 border-b border-gray-200">
                <p className="text-gray-600">Request ID:</p>
                <p className="font-medium">{designRequest._id}</p>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-3 border-b border-gray-200">
                <p className="text-gray-600">Package Type:</p>
                <p className="font-medium capitalize">{designRequest.packageType}</p>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-3 border-b border-gray-200">
                <p className="text-gray-600">Status:</p>
                <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800 capitalize">
                  {designRequest.status}
                </span>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-3 border-b border-gray-200">
                <p className="text-gray-600">Submitted On:</p>
                <p className="font-medium">{new Date(designRequest.createdAt).toLocaleDateString()}</p>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-3 border-b border-gray-200">
                <p className="text-gray-600">Payment Status:</p>
                <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                  designRequest.paymentStatus === 'paid' 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-orange-100 text-orange-800'
                } capitalize`}>
                  {designRequest.paymentStatus}
                </span>
              </div>
            </div>
          </div>
          
          <div className="bg-teal-50 p-6 rounded-lg mb-8">
            <h2 className="text-xl font-semibold text-teal-800 mb-3">Next Steps</h2>
            <ol className="list-decimal pl-5 space-y-2">
              <li>Our design team will review your floor plan within 1-2 business days</li>
              <li>You'll receive an email notification when your design concept is ready for review</li>
              <li>Review the design concept and provide any feedback for revisions</li>
              <li>Once approved, we'll proceed to finalize your Design</li>
            </ol>
          </div>
          
          {designRequest.paymentStatus === 'pending' && (
            <div className="bg-yellow-50 border border-yellow-200 p-6 rounded-lg mb-8">
              <h3 className="font-semibold text-yellow-800 mb-2">Payment Required</h3>
              <p className="mb-4">Please complete your payment to proceed with your design request.</p>
              <div className="flex items-center justify-between">
                <p className="font-bold text-lg">${designRequest.designFee}.00</p>
                <Link 
                  to={`/checkout/design-request/${designRequest._id}`}
                  className="bg-teal-800 text-white px-6 py-2 rounded hover:bg-teal-900 transition-all"
                >
                  Complete Payment
                </Link>
              </div>
            </div>
          )}
          
          <div className="flex flex-col sm:flex-row justify-between space-y-4 sm:space-y-0 sm:space-x-4">
            <Link 
              to="/account/design-requests"
              className="text-center py-2 px-4 border border-gray-300 rounded hover:bg-gray-50 transition-all"
            >
              View All Design Requests
            </Link>
            <Link 
              to="/"
              className="text-center bg-teal-800 text-white py-2 px-6 rounded hover:bg-teal-900 transition-all"
            >
              Continue Shopping
            </Link>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default CustomDesignConfirmation;